import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import SearchBar from "../../components/SearchBar";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  IconButton,
  Button,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
} from "@mui/material";
import AddNotification from "../../Modals/addNotification";
import AddIcon from "@mui/icons-material/Add";
import NotificationsList from "../../features/manageNotifications/notificationsList";
import usePermission from "../../hooks/usePermission";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import useTranslation from "../../hooks/traslation";
import {
  useAddNotificationMutation,
  useLazyGetNotificationQuery,
  useDeleteNotificationByIdMutation,
} from "../../services/notification";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import moment from "moment";
import { NOTIFICATION_ROLE, NOTIFICATION_TYPE } from "../../utils/enums";
import { isValidInput } from "../../utils/validations";

const ManageNotifications = () => {
  const { isAdd, isDelete } = usePermission({
    label: "Manage Notifications",
    page_permission: "isView",
  });

  const navigate = useNavigate();
  const translation = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [openModal, setOpenModal] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const handleCloseModal = () => {
    setOpenModal(false);
  };
  const [addNotification] = useAddNotificationMutation();
  const [getNotification] = useLazyGetNotificationQuery();
  const [deleteNotification] = useDeleteNotificationByIdMutation();
  const [selectedType, setSelectedType] = useState<string | NOTIFICATION_TYPE>(
    ""
  );
  const [selectedRole, setSelectedRole] = useState<string | NOTIFICATION_ROLE>(
    ""
  );
  const [filteredRows, setFilteredRows] = useState<any[]>([]);

  const handlePageChange = (event: any, newPage: number) => {
    const updatedPage = newPage + 1;
    setPage(updatedPage);

    const params = new URLSearchParams(window.location.search);
    params.set("page", updatedPage.toString());

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, "", newUrl);
  };

  const getNotificationData = async () => {
    try {
      setIsLoading(true);
      const res = await getNotification({
        page,
        limit: limit,
        search: debouncedSearchTerm,
        type: 2,
        filterType: selectedType ? Number(selectedType) : undefined,
        role: selectedRole ? Number(selectedRole) : undefined,
      }).unwrap();
      if (res.statusCode === 200) {
        setCount(res?.data?.totalNotification);
        setRows(res?.data?.notificationData || []);
        setFilteredRows(res?.data?.notificationData || []);
      }
    } catch (error: any) {
      showError(error?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deleteNotification({
        id: selectedId || "",
      }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast("notification deleted");
        if (filteredRows.length === 1 && page > 1) {
          setPage(page - 1);
        } else {
          getNotificationData();
        }
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  const handleAddNotification = async (values: any) => {
    try {
      setIsLoading(true);
      const payload = {
        title: values.title,
        message: values.message,
        role: parseInt(values.role),
        type: parseInt(values.type),
      };
      console.log(payload, "payLoad");

      const response = await addNotification(payload).unwrap();
      if (response?.statusCode === 200) {
        showToast("Notification added successfully");
        setOpenModal(false);
        await getNotificationData();
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to add notification");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getNotificationData();
  }, [page, debouncedSearchTerm, limit, selectedType, selectedRole]);

  useEffect(() => {
    let filtered = [...rows];
    if (selectedType) {
      filtered = filtered.filter(
        (row) => row.notificationType === Number(selectedType)
      );
    }
    if (selectedRole) {
      filtered = filtered.filter((row) => row.role === Number(selectedRole));
    }
    setFilteredRows(filtered);
  }, [rows, selectedType, selectedRole]);

  const columns: GridColDef<any>[] = [
    {
      flex: 1,
      minWidth: 150,
      field: "title",
      headerName: "Title",
      editable: false,
      sortable: false,
    },
    {
      flex: 2,
      minWidth: 200,
      field: "message",
      headerName: "Message",
      editable: false,
      sortable: false,
    },
    {
      flex: 1,
      minWidth: 150,
      field: "notificationType",
      headerName: "Type",
      editable: false,
      sortable: false,
      valueGetter: ({ row }) => {
        switch (row.notificationType) {
          case NOTIFICATION_TYPE.BROADCAST:
            return "Broadcast";
          case NOTIFICATION_TYPE.MAIL:
            return "Mail";
          default:
            return "";
        }
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "role",
      headerName: "Send To",
      editable: false,
      sortable: false,
      valueGetter: ({ row }) => {
        switch (row.role) {
          case NOTIFICATION_ROLE.ALL:
            return "All";
          case NOTIFICATION_ROLE.EMPLOYER:
            return "Employer";
          case NOTIFICATION_ROLE.FREELANCER:
            return "Freelancer";
          default:
            return "";
        }
      },
    },
    {
      minWidth: 150,
      field: "createdAt",
      headerName: "Created Date",
      editable: false,
      sortable: false,
      valueGetter: (params) =>
        moment(params.row.createdAt).format("DD MMMM YYYY, hh:mm A"),
    },
    {
      minWidth: 100,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            {isDelete ? (
              <Box>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      setSelectedId(row?._id);
                      setOpenDelete(true);
                    }}
                  >
                    <img src="/static/images/trash2_icon.svg" alt="" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </div>
        );
      },
    },
  ];

  const notificationTypes = [
    { value: "", label: "All" },
    { value: NOTIFICATION_TYPE.BROADCAST, label: "Broadcast" },
    { value: NOTIFICATION_TYPE.MAIL, label: "Mail" },
  ];

  const roleOptions = [
    { value: "", label: "All" },
    { value: NOTIFICATION_ROLE.EMPLOYER, label: "Employer" },
    { value: NOTIFICATION_ROLE.FREELANCER, label: "Freelancer" },
  ];

  return (
    <MainContainer>
      <div className="main_title">
        <h1>{translation.main.manageNotification}</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translation.dashboard.dashboard}
          </span>{" "}
          - {translation.main.manageNotification}
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div
            className="left_s"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <SearchBar
              placeholder={translation.user.SearchHere}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              value={searchTerm}
              onCross={() => setSearchTerm("")}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
            />
          </div>
          {isAdd ? (
            <div
              className="right_s"
              style={{ display: "flex", alignItems: "center", gap: "16px" }}
            >
              <Autocomplete
                size="small"
                options={notificationTypes}
                value={
                  notificationTypes.find(
                    (type) => type.value === selectedType
                  ) || null
                }
                onChange={(_, newValue) => {
                  setSelectedType(newValue?.value || "");
                }}
                getOptionLabel={(option) => option.label}
                className="users_autocomplete"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type"
                    size="small"
                    sx={{ width: "150px" }}
                  />
                )}
              />
              <Autocomplete
                size="small"
                options={roleOptions}
                value={
                  roleOptions.find((role) => role.value === selectedRole) ||
                  null
                }
                onChange={(_, newValue) => {
                  setSelectedRole(newValue?.value || "");
                }}
                getOptionLabel={(option) => option.label}
                className="users_autocomplete"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Role"
                    size="small"
                    sx={{ width: "150px" }}
                  />
                )}
              />
              <Button
                className="btn btn_primary"
                onClick={() => setOpenModal(true)}
              >
                <AddIcon /> {translation.main.addNotification}
              </Button>
            </div>
          ) : null}
        </div>
        <div className="sorting_table">
          <TableComponent
            columns={columns}
            rows={filteredRows}
            loading={isLoading}
            count={count}
            page={page}
            onPageChange={handlePageChange}
          />
          {filteredRows.length !== 0 && (
            <div className="table_footer">
              <div className="pagination_info">
                <span className="total_records">Showing {filteredRows.length} of {count} records</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        text={"Are you sure you want to delete this Notification"}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
      />
      <AddNotification
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        onSubmit={handleAddNotification}
        isLoading={isLoading}
      />
    </MainContainer>
  );
};

export default ManageNotifications;
